/**
 * Resources Page Functionality
 * Handles filtering products by resource type and search
 */

class ResourcesPage {
  constructor() {
    this.searchInput = document.getElementById('resources-search-input');
    this.searchClear = document.querySelector('.resources-search__clear');
    this.filtersToggle = document.getElementById('filters-toggle');
    this.filtersSidebar = document.getElementById('resources-filters');
    this.clearFiltersBtn = document.getElementById('clear-filters');
    this.clearFiltersEmptyBtn = document.getElementById('clear-filters-empty');
    this.resourcesGrid = document.getElementById('resources-grid');
    this.resourcesEmpty = document.getElementById('resources-empty');
    this.resourcesCount = document.getElementById('resources-count-number');
    this.resourcesCountText = document.getElementById('resources-count-text');
    
    this.productCards = document.querySelectorAll('[data-product]');
    this.filterInputs = document.querySelectorAll('.resources-filter-option__input');
    this.filterGroups = document.querySelectorAll('.resources-filter-group');
    
    this.activeFilters = {
      search: '',
      resourceType: [],
      collection: []
    };
    
    this.init();
  }
  
  init() {
    // Search functionality
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => this.handleSearch(e.target.value));
    }
    
    if (this.searchClear) {
      this.searchClear.addEventListener('click', () => this.clearSearch());
    }
    
    // Filter toggle (mobile)
    if (this.filtersToggle) {
      this.filtersToggle.addEventListener('click', () => this.toggleFilters());
    }
    
    // Filter inputs
    this.filterInputs.forEach(input => {
      input.addEventListener('change', () => this.handleFilterChange());
    });
    
    // Clear filters
    if (this.clearFiltersBtn) {
      this.clearFiltersBtn.addEventListener('click', () => this.clearAllFilters());
    }
    
    if (this.clearFiltersEmptyBtn) {
      this.clearFiltersEmptyBtn.addEventListener('click', () => this.clearAllFilters());
    }
    
    // Filter group toggles
    this.filterGroups.forEach(group => {
      const header = group.querySelector('.resources-filter-group__header');
      if (header) {
        header.addEventListener('click', () => this.toggleFilterGroup(header));
      }
    });
    
    // Downloads toggle buttons
    this.initDownloadsToggles();
    
    // Update filter counts on init
    this.updateFilterCounts();
    
    // Initial filter
    this.filterProducts();
    
    // Ensure resource download links work properly
    this.initResourceLinks();
  }
  
  initResourceLinks() {
    // Ensure resource card links open properly (handles CDN URLs)
    const resourceLinks = document.querySelectorAll('.product-resource-card__link[data-resource-link="true"]');
    resourceLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        // Only proceed if it's a valid URL (not '#')
        if (href && href !== '#' && !href.startsWith('#')) {
          // Let the browser handle the link naturally
          // The target="_blank" will open in new tab
          // No need to preventDefault for CDN URLs
        } else {
          e.preventDefault();
        }
      });
    });
  }
  
  initDownloadsToggles() {
    const downloadToggles = document.querySelectorAll('[data-downloads-toggle]');
    downloadToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        
        const productId = toggle.getAttribute('data-product-id');
        if (!productId) return;
        
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        const productCard = toggle.closest('product-card.product-card--resources');
        
        if (!productCard) return;
        
        // Find the overlay content specific to this product
        const overlay = productCard.querySelector(`[data-downloads-content][data-product-id="${productId}"]`);
        
        if (overlay) {
          if (isExpanded) {
            this.closeDownloadsOverlay(toggle, overlay);
          } else {
            this.openDownloadsOverlay(toggle, overlay);
          }
        }
      });
    });
    
    // Close buttons
    const closeButtons = document.querySelectorAll('[data-downloads-close]');
    closeButtons.forEach(closeBtn => {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        
        const overlay = closeBtn.closest('[data-downloads-content]');
        const productCard = overlay?.closest('product-card.product-card--resources');
        const toggle = productCard?.querySelector('[data-downloads-toggle]');
        
        if (overlay && toggle) {
          this.closeDownloadsOverlay(toggle, overlay);
        }
      });
    });
    
    // Close overlay when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-downloads-content]') && !e.target.closest('[data-downloads-toggle]')) {
        const openOverlays = document.querySelectorAll('[data-downloads-content][style*="block"]');
        openOverlays.forEach(overlay => {
          const productCard = overlay.closest('product-card.product-card--resources');
          const toggle = productCard?.querySelector('[data-downloads-toggle]');
          if (toggle) {
            this.closeDownloadsOverlay(toggle, overlay);
          }
        });
      }
    });
  }
  
  openDownloadsOverlay(toggle, overlay) {
    toggle.setAttribute('aria-expanded', 'true');
    overlay.style.display = 'flex';
  }
  
  closeDownloadsOverlay(toggle, overlay) {
    toggle.setAttribute('aria-expanded', 'false');
    overlay.style.display = 'none';
  }
  
  handleSearch(value) {
    this.activeFilters.search = value.toLowerCase().trim();
    
    // Show/hide clear button
    if (this.searchClear) {
      this.searchClear.style.display = this.activeFilters.search ? 'flex' : 'none';
    }
    
    this.filterProducts();
  }
  
  clearSearch() {
    if (this.searchInput) {
      this.searchInput.value = '';
      this.handleSearch('');
    }
  }
  
  handleFilterChange() {
    // Collect active filters
    this.activeFilters.resourceType = [];
    this.activeFilters.collection = [];
    
    this.filterInputs.forEach(input => {
      if (input.checked) {
        const filterType = input.dataset.filter;
        const value = input.value;
        
        if (filterType === 'resource-type') {
          this.activeFilters.resourceType.push(value);
        } else if (filterType === 'collection') {
          this.activeFilters.collection.push(value);
        }
      }
    });
    
    // Show/hide clear filters button
    const hasActiveFilters = 
      this.activeFilters.resourceType.length > 0 ||
      this.activeFilters.collection.length > 0;
    
    if (this.clearFiltersBtn) {
      this.clearFiltersBtn.style.display = hasActiveFilters ? 'block' : 'none';
    }
    
    this.updateFilterCounts();
    this.filterProducts();
  }
  
  filterProducts() {
    let visibleCount = 0;
    
    this.productCards.forEach(card => {
      let isVisible = true;
      
      // Search filter
      if (this.activeFilters.search) {
        const title = card.dataset.title || '';
        const vendor = card.dataset.vendor || '';
        const searchTerm = this.activeFilters.search;
        
        if (!title.includes(searchTerm) && !vendor.includes(searchTerm)) {
          isVisible = false;
        }
      }
      
      // Resource type filter
      if (isVisible && this.activeFilters.resourceType.length > 0) {
        const cardResourceTypes = card.dataset.resourceTypes || '';
        const hasMatchingResourceType = this.activeFilters.resourceType.some(filterType => {
          return cardResourceTypes.includes(filterType);
        });
        
        if (!hasMatchingResourceType) {
          isVisible = false;
        }
      }
      
      // Collection filter
      if (isVisible && this.activeFilters.collection.length > 0) {
        const cardCollection = card.dataset.collection || '';
        if (!this.activeFilters.collection.includes(cardCollection)) {
          isVisible = false;
        }
      }
      
      // Show/hide card
      card.dataset.hidden = !isVisible;
      card.style.display = isVisible ? 'block' : 'none';
      
      // Hide/show resource cards and list items within visible cards
      if (isVisible && this.activeFilters.resourceType.length > 0) {
        // Hide/show resource cards
        const resourceCards = card.querySelectorAll('.product-resource-card[data-resource-type]');
        resourceCards.forEach(resourceCard => {
          const cardType = resourceCard.dataset.resourceType;
          const shouldShow = this.activeFilters.resourceType.includes(cardType);
          resourceCard.dataset.hidden = !shouldShow;
          resourceCard.style.display = shouldShow ? 'block' : 'none';
        });
        
        // Hide/show resource list items in accordion
        const resourceListItems = card.querySelectorAll('.product-resource-list-item[data-resource-type]');
        resourceListItems.forEach(item => {
          const itemType = item.dataset.resourceType;
          const shouldShow = this.activeFilters.resourceType.includes(itemType);
          item.dataset.hidden = !shouldShow;
          item.style.display = shouldShow ? 'flex' : 'none';
        });
      } else if (isVisible) {
        // Show all resource cards and list items if no resource type filter
        const resourceCards = card.querySelectorAll('.product-resource-card[data-resource-type]');
        resourceCards.forEach(resourceCard => {
          resourceCard.dataset.hidden = false;
          resourceCard.style.display = 'block';
        });
        
        const resourceListItems = card.querySelectorAll('.product-resource-list-item[data-resource-type]');
        resourceListItems.forEach(item => {
          item.dataset.hidden = false;
          item.style.display = 'flex';
        });
      }
      
      if (isVisible) {
        visibleCount++;
      }
    });
    
    // Show/hide empty state
    if (this.resourcesEmpty) {
      this.resourcesEmpty.style.display = visibleCount === 0 ? 'block' : 'none';
    }
    
    if (this.resourcesGrid) {
      this.resourcesGrid.style.display = visibleCount === 0 ? 'none' : 'grid';
    }
    
    // Update count
    this.updateProductCount(visibleCount);
  }
  
  updateProductCount(count) {
    if (this.resourcesCount) {
      this.resourcesCount.textContent = count;
    }
    
    if (this.resourcesCountText) {
      this.resourcesCountText.textContent = count === 1 ? 'product' : 'products';
    }
  }
  
  updateFilterCounts() {
    // Reset counts
    const countElements = document.querySelectorAll('.resources-filter-option__count');
    countElements.forEach(el => {
      el.textContent = '0';
    });
    
    // Calculate counts for each filter option
    this.filterInputs.forEach(input => {
      const filterType = input.dataset.filter;
      const value = input.value;
      const countElement = input.closest('.resources-filter-option')?.querySelector(`[data-count="${filterType}-${value}"]`);
      
      if (countElement) {
        // Count products matching this filter value
        let count = 0;
        this.productCards.forEach(card => {
          let matches = true;
          
          // Check if card matches current search
          if (this.activeFilters.search) {
            const title = card.dataset.title || '';
            const vendor = card.dataset.vendor || '';
            if (!title.includes(this.activeFilters.search) && !vendor.includes(this.activeFilters.search)) {
              matches = false;
            }
          }
          
          // Check if card matches this filter value
          if (matches) {
            if (filterType === 'resource-type') {
              const cardResourceTypes = card.dataset.resourceTypes || '';
              if (cardResourceTypes.includes(value)) {
                count++;
              }
            } else if (filterType === 'collection') {
              const cardCollection = card.dataset.collection || '';
              if (cardCollection === value) {
                count++;
              }
            }
          }
        });
        
        countElement.textContent = count;
      }
    });
  }
  
  clearAllFilters() {
    // Clear search
    this.clearSearch();
    
    // Uncheck all filters
    this.filterInputs.forEach(input => {
      input.checked = false;
    });
    
    // Reset active filters
    this.activeFilters.resourceType = [];
    this.activeFilters.collection = [];
    
    // Hide clear button
    if (this.clearFiltersBtn) {
      this.clearFiltersBtn.style.display = 'none';
    }
    
    // Show all resource cards and list items
    this.productCards.forEach(card => {
      const resourceCards = card.querySelectorAll('.product-resource-card[data-resource-type]');
      resourceCards.forEach(resourceCard => {
        resourceCard.dataset.hidden = false;
        resourceCard.style.display = 'block';
      });
      
      const resourceListItems = card.querySelectorAll('.product-resource-list-item[data-resource-type]');
      resourceListItems.forEach(item => {
        item.dataset.hidden = false;
        item.style.display = 'flex';
      });
    });
    
    // Update and filter
    this.updateFilterCounts();
    this.filterProducts();
  }
  
  toggleFilters() {
    if (this.filtersSidebar) {
      const isOpen = this.filtersSidebar.dataset.open === 'true';
      this.filtersSidebar.dataset.open = !isOpen;
    }
  }
  
  toggleFilterGroup(header) {
    const isExpanded = header.getAttribute('aria-expanded') === 'true';
    header.setAttribute('aria-expanded', !isExpanded);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.getElementById('resources-grid')) {
      new ResourcesPage();
    }
  });
} else {
  if (document.getElementById('resources-grid')) {
    new ResourcesPage();
  }
}
